import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

/**
 * QueryProvider - Wraps the app with React Query context
 * 
 * React Query provides:
 * - Automatic caching of API responses
 * - Background refetching to keep data fresh
 * - Automatic retries on failed requests
 * - Request deduplication (multiple components requesting same data = 1 API call)
 */

// Create a client instance with custom configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache data for 10 minutes before considering it "stale"
      // Longer stale time = fewer unnecessary refetches during normal browsing
      // We rely on manual invalidation (queryClient.invalidateQueries) 
      // after mutations (save/unsave) for immediate updates
      staleTime: 10 * 60 * 1000, // 10 minutes in milliseconds
      
      // Keep unused data in cache for 10 minutes before garbage collection
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly called 'cacheTime')
      
      // Retry failed requests up to 1 time (default is 3)
      retry: 1,
      
      // Refetch on window focus to sync data across tabs/devices
      refetchOnWindowFocus: true,
      
      // Don't refetch on component mount if data is fresh
      // Rely on cache + manual invalidation instead
      refetchOnMount: false,
    },
  },
});

interface QueryProviderProps {
  children: React.ReactNode;
}

/**
 * Wrap your app with this provider to enable React Query caching
 */
export const QueryProvider: React.FC<QueryProviderProps> = ({ children }) => {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
    </QueryClientProvider>
  );
};

export default QueryProvider;

